import { useCallback } from 'react';

import EnterFilePasswordForm from '../../../components/Forms/EnterFilePasswordForm';
import { FilePasswordForm } from '../../../components/Forms/FilePasswordForm';
import { SecretFileForm } from '../../../components/Forms/SecretFileForm';
import { ApiErrorEnum } from '../../../enums/ApiErrorEnum';
import type File from '../../../interfaces/File';
import type { DecryptedFileSecretData, SecretFilePresetType } from '../../../interfaces/FileSecret';
import { getApiErrorCode } from '../../../utils/apiErrorCode';
import { handleFilePasswordError } from '../../../utils/filePasswordErrors';
import { encryptFileSecret } from '../../../utils/fileSecretCrypto';
import { serializeSecretFileData } from '../../../utils/fileSecretPresets';
import { openSecretFileModal } from '../../../utils/openSecretFileModal';
import type { UseFilesActionsDeps } from '../deps';
import { fileService, foldersService, functionService } from '../services';
import type { UseFilesActionsEvents, UseFilesActionsReturn } from '../types';

/**
 * Provides actions and modal flows for secret files and folder/file password management.
 */
export function useFilesPasswordActions(
  deps: UseFilesActionsDeps,
): Pick<
  UseFilesActionsReturn,
  | 'handleCreateSecretFile'
  | 'handleViewSecretFile'
  | 'handleDownloadEncryptedFile'
  | 'handleSetFilePassword'
  | 'handleEditFilePassword'
  | 'handleRemoveFilePassword'
  | 'handleLockFiles'
  | 'handleUnlockFolder'
> {
  const {
    folderUidRef,
    uploadFiles,
    modal,
    notification,
    message,
    t,
    navigate,
    location,
    authenticatedUser,
    isAdminOrContributor,
    getFilePasswordSession,
    removeFilePasswordSession,
  } = deps;

  const createSecretFile = useCallback(
    async (data: DecryptedFileSecretData, password?: string, _events?: UseFilesActionsEvents) => {
      try {
        let fileContent: string;

        const updatedAt = new Date().toISOString();
        if (password) {
          const fieldsJson = JSON.stringify(data.fields);
          const encryptedFields = await encryptFileSecret(fieldsJson, password);
          fileContent = JSON.stringify(
            serializeSecretFileData(
              { ...data, encrypted: true, schemaVersion: 2, updatedAt },
              encryptedFields,
            ),
          );
        } else {
          fileContent = JSON.stringify(
            serializeSecretFileData(
              { ...data, encrypted: false, schemaVersion: 2, updatedAt },
              data.fields,
            ),
          );
        }

        const blob = new Blob([fileContent], { type: 'application/json' });
        const fileName = `${data.name}.fgsecret`;
        const file = new File([blob], fileName, { type: 'application/json' });

        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);

        uploadFiles(
          dataTransfer.files,
          true,
          folderUidRef.current ? parseInt(folderUidRef.current) : undefined,
        );

        message.success({
          content: t('notifications.fileCreated', { resource: fileName }),
        });
      } catch (err) {
        console.error('[createSecretFile] Error:', err);
        notification.error({
          title: t('errors.genericCreateError', { resource: t('common.file') }),
        });
      }
    },
    [folderUidRef, t, notification, uploadFiles, message],
  );

  const handleCreateSecretFile = useCallback(
    (preset: SecretFilePresetType = 'credentials', events?: UseFilesActionsEvents) => {
      if (!isAdminOrContributor) {
        notification.error({
          title: t('errors.noUploadPermission'),
        });
        return;
      }

      const modalInstance = modal.confirm({
        title: t(`secret.presets.${preset}`),
        width: 600,
        content: (
          <SecretFileForm
            preset={preset}
            onSubmit={(data, password) => {
              createSecretFile(data, password, events);
              modalInstance.destroy();
            }}
            onCancel={() => {
              modalInstance.destroy();
            }}
          />
        ),
        footer: null,
      });
    },
    [modal, t, notification, createSecretFile, isAdminOrContributor],
  );

  /** Open secret detail modal (shared with click → openFileSafely path). */
  const handleViewSecretFile = useCallback(
    (file: File) => {
      openSecretFileModal(modal, file);
    },
    [modal],
  );

  /**
   * Downloads an encrypted PDF version of the file.
   * If "free" is not 0, downloads with the original file name.
   */
  const handleDownloadEncryptedFile = async (file: File, free = 0) => {
    try {
      // Get a file token for a protected download
      const fileToken = await fileService.getFileForViewer(file.id, {
        type: 'pdfe',
      });

      // Retrieve the download blob using the token and save it
      const blob = await functionService.getFileBlob(fileToken.token);
      if (blob) {
        const href = URL.createObjectURL(blob);
        const link = document.createElement('a');
        // Adjust file extension on download if needed
        if (free === 0) link.download = file.fileName + '.pdf';
        else link.download = file.fileName;
        link.href = href;
        link.click();
        URL.revokeObjectURL(href);
      }
    } catch (error) {
      const errorCode = getApiErrorCode((error as { response?: unknown })?.response);
      if (errorCode === ApiErrorEnum.FileSizeViewerOver) {
        notification.error({
          title: t('errors.fileSizeTooBigForDownload'),
        });
        return;
      }
      notification.error({
        title: t('errors.hitASnag'),
      });
    }
  };

  /**
   * Open modal for setting, editing, or removing a file password.
   */
  const openFilePasswordFormModal = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      const modalInstance = modal.confirm({
        title: t('modal.filePasswordForm.title'),
        width: 600,
        content: (
          <FilePasswordForm
            file={file}
            onSubmit={(hasPassword, ownerBypass) => {
              modalInstance.destroy();
              // Prepare updatedFile with new password settings
              const updatedFile = { ...file, isPasswordSet: hasPassword };
              // Owner can choose to bypass password
              if (
                authenticatedUser &&
                file.ownerId === authenticatedUser.id &&
                ownerBypass !== undefined
              ) {
                updatedFile.isPasswordBypass = ownerBypass;
              }
              // Update parent via events
              events?.onFilesUpdated?.([updatedFile]);
            }}
            onCancel={() => {
              modalInstance.destroy();
              // On cancel, navigate back to main section if applicable
              const parts = location.pathname.split('/');
              const section = parts[1] === 'files' && parts[2] ? parts[2] : null;
              if (section) {
                setTimeout(() => {
                  navigate(`/files/${section}`, { replace: true });
                }, 0);
              }
            }}
          />
        ),
        footer: null,
      });
    },
    [modal, t, location.pathname, navigate, authenticatedUser],
  );

  /**
   * Initiates process for setting (or updating) a file/folder password,
   * first checking password requirements and lockouts.
   */
  const setFilePassword = useCallback(
    async (file: File, events?: UseFilesActionsEvents) => {
      let checkResponse;
      try {
        checkResponse = await foldersService.checkFolderPassword(file.id);
      } catch (_err) {
        notification.error({
          title: t('errors.genericError'),
          message: t('errors.genericFetchError', { resource: t('common.folder') }),
        });
        return;
      }

      // Warn user if locked out due to too many failed attempts
      if (checkResponse.isLockedOut && (checkResponse.lockoutMinutesRemaining ?? 0) > 0) {
        notification.warning({
          message: t('modal.enterFolderPasswordForm.lockout', {
            minutes: checkResponse.lockoutMinutesRemaining ?? 0,
          }),
        });
        return;
      }

      // If password is required, open a password entry modal first
      if (checkResponse.passwordRequired) {
        const passwordModalInstance = modal.confirm({
          title: t('modal.filePasswordForm.title'),
          width: 480,
          content: (
            <EnterFilePasswordForm
              fileId={file.id}
              isOwner={authenticatedUser?.id === file.ownerId}
              onCloseParentModal={() => passwordModalInstance.destroy()}
              onSubmit={() => {
                passwordModalInstance.destroy();
                openFilePasswordFormModal(file, events);
              }}
              onCancel={() => passwordModalInstance.destroy()}
            />
          ),
          footer: null,
        });
      } else {
        // Can open password modal directly if no password lock
        openFilePasswordFormModal(file, events);
      }
    },
    [modal, notification, t, openFilePasswordFormModal],
  );

  /**
   * Remove a password from a file/folder, after confirming the user's password.
   */
  const removeFilePassword = useCallback(
    async (file: File, currentPassword: string, events?: UseFilesActionsEvents) => {
      try {
        // Attempt to delete the password
        await foldersService.deleteFolderPassword(file.id, { currentPassword });
        // Remove session cache
        removeFilePasswordSession(file.id);
        message.success(t('notifications.folderPasswordRemoved'));
        // Notify updated file password state
        events?.onFilesUpdated?.([{ ...file, isPasswordSet: false }]);
      } catch (error: unknown) {
        // If handled, do not show further errors
        if (handleFilePasswordError(error)) return;
        // Otherwise, show a generic error notification
        const err = error as { response?: { data?: { message?: string } }; message?: string };
        notification.error({
          title: t('errors.genericUpdateError', { resource: t('common.folder') }),
          message: err?.response?.data?.message || err?.message || t('errors.genericError'),
        });
        throw error;
      }
    },
    [notification, message, t, removeFilePasswordSession],
  );

  /**
   * Check and show the modal to set a password for a folder (not for files).
   */
  const handleSetFilePassword = useCallback(
    async (file: File, events?: UseFilesActionsEvents) => {
      if (!file.isFolder) {
        notification.error({
          title: t('errors.genericError'),
          message: t('errors.notAFolder'),
        });
        return;
      }
      setFilePassword(file, events);
    },
    [notification, t, setFilePassword],
  );

  /**
   * Begin modal flow for editing (changing) a folder's password.
   * User must first verify current password.
   */
  const handleEditFilePassword = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      if (!file.isFolder) {
        notification.error({
          title: t('errors.genericError'),
          message: t('errors.notAFolder'),
        });
        return;
      }
      // Open password entry modal for verification
      const passwordModalInstance = modal.confirm({
        title: t('modal.filePasswordForm.title'),
        width: 480,
        content: (
          <EnterFilePasswordForm
            fileId={file.id}
            isOwner={authenticatedUser?.id === file.ownerId}
            onCloseParentModal={() => passwordModalInstance.destroy()}
            onSubmit={() => {}}
            onSubmitAfterVerify={() => {
              passwordModalInstance.destroy();
              // Once verified, open new password setting modal
              openFilePasswordFormModal(file, events);
            }}
            onCancel={() => passwordModalInstance.destroy()}
          />
        ),
        footer: null,
      });
    },
    [modal, notification, t, openFilePasswordFormModal, authenticatedUser?.id],
  );

  /**
   * Handle the modal flow for removing a folder's password.
   * Verifies password and asks for removal confirmation.
   */
  const handleRemoveFilePassword = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      if (!file.isFolder) {
        notification.error({
          title: t('errors.genericError'),
          message: t('errors.notAFolder'),
        });
        return;
      }
      // Prompt for current password before removal
      const firstModalInstance = modal.confirm({
        title: t('modal.filePasswordForm.title'),
        width: 480,
        content: (
          <EnterFilePasswordForm
            fileId={file.id}
            isOwner={authenticatedUser?.id === file.ownerId}
            onCloseParentModal={() => firstModalInstance.destroy()}
            onSubmit={() => {}}
            onSubmitWithPassword={(currentPassword) => {
              firstModalInstance.destroy();
              // Confirm removal in a second modal
              modal.confirm({
                title: t('modal.filePasswordForm.remove'),
                content: t('modal.filePasswordForm.removeConfirm'),
                okText: t('common.remove'),
                okButtonProps: { danger: true },
                onOk: () => removeFilePassword(file, currentPassword, events),
              });
            }}
            onCancel={() => firstModalInstance.destroy()}
          />
        ),
        footer: null,
      });
    },
    [modal, notification, t, removeFilePassword, authenticatedUser?.id],
  );

  /**
   * Remove file password session info for multiple files.
   * Shows a notification if any were "locked".
   */
  const handleLockFiles = useCallback(
    (files: File[], _events?: UseFilesActionsEvents) => {
      let removedCount = 0;
      files.forEach((file) => {
        if (getFilePasswordSession(file.id)) {
          removeFilePasswordSession(file.id);
          removedCount += 1;
        }
      });
      if (removedCount > 0) {
        message.success(t('notifications.folderLocked', { count: removedCount }));
      }
    },
    [getFilePasswordSession, removeFilePasswordSession, message, t],
  );

  /**
   * Prompt a modal to unlock a password-protected folder.
   */
  const handleUnlockFolder = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      if (!file.isFolder) return;
      const passwordModalInstance = modal.confirm({
        title: t('modal.filePasswordForm.title'),
        width: 480,
        content: (
          <EnterFilePasswordForm
            fileId={file.id}
            isOwner={authenticatedUser?.id === file.ownerId}
            onCloseParentModal={() => passwordModalInstance.destroy()}
            onSubmit={() => {
              passwordModalInstance.destroy();
              message.success(t('notifications.folderUnlocked'));
              events?.onFilesUpdated?.([file]);
            }}
            onCancel={() => passwordModalInstance.destroy()}
          />
        ),
        footer: null,
      });
    },
    [modal, message, t, authenticatedUser?.id],
  );

  return {
    handleCreateSecretFile,
    handleViewSecretFile,
    handleDownloadEncryptedFile,
    handleSetFilePassword,
    handleEditFilePassword,
    handleRemoveFilePassword,
    handleLockFiles,
    handleUnlockFolder,
  };
}
